import React, { useId } from 'react';
import { Autocomplete as MuiAutocomplete, Box, InputLabel, TextField, Button } from '@mui/material';
import * as styles from './Autocomplete.scss';

const Autocomplete = (props) => {
  const {
    className,
    deleteIcon,
    disableClearable,
    disableCloseOnSelect,
    disabled,
    disablePortal = true,
    enableSelectAll = false,
    error,
    errorText,
    filterSelectedOptions,
    getOptionDisabled,
    getOptionKey,
    getOptionLabel,
    id,
    isOptionEqualToValue,
    label,
    loading,
    multiple,
    onChange,
    options,
    placeholder,
    readOnly,
    renderInput,
    renderOption,
    required,
    size = 'small',
    sx,
    value,
  } = props;

  const generatedId = useId();

  const handleSelectAll = () => {
    if (onChange && multiple && options) {
      // Get all non-disabled options
      const selectableOptions = options.filter((option) => !getOptionDisabled || !getOptionDisabled(option));
      onChange(null, selectableOptions);
    }
  };

  const renderOptionWithSelectAll = (props, option, state) => {
    const { key, ...otherProps } = props;
    // If this is the first option, render the select all button too
    if (state.index === 0) {
      return (
        <Box key="select-all-container" className={styles.selectAllContainer}>
          <Button
            className={styles.selectAllButton}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectAll();
            }}
            size="small"
            variant="text"
          >
            Select All
          </Button>
          <Box key="select-all-divider" className={styles.optionDivider} />
          <Box {...otherProps} key={key} className={styles.optionItem}>
            {renderOption ? renderOption(props, option, state) : getOptionLabel ? getOptionLabel(option) : option}
          </Box>
        </Box>
      );
    }

    // For subsequent options, adjust the index to account for the select all button
    const adjustedState = {
      ...state,
      index: state.index - 1,
    };

    return renderOption ? renderOption(props, option, adjustedState) : (
      <Box {...otherProps} key={key} className={styles.optionItem}>
        {getOptionLabel ? getOptionLabel(option) : option}
      </Box>
    );
  };

  return (
    <Box className={styles.container} sx={sx}>
      {label && <InputLabel htmlFor={id ?? generatedId} shrink={false} error={error} required={required}>{label}</InputLabel>}
      <MuiAutocomplete
        id={id ?? generatedId}
        value={value}
        options={options ?? []}
        className={className}
        disabled={disabled}
        disableClearable={disableClearable}
        disableCloseOnSelect={disableCloseOnSelect}
        disablePortal={disablePortal}
        filterSelectedOptions={filterSelectedOptions}
        getOptionDisabled={getOptionDisabled}
        getOptionKey={getOptionKey}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        loading={loading}
        multiple={multiple}
        onChange={onChange}
        readOnly={readOnly}
        renderInput={renderInput ?? ((params) => (
          <TextField
            {...params}
            error={error}
            helperText={errorText}
            placeholder={loading ? 'Loading...' : placeholder}
            disabled={readOnly}
          />))}
        renderOption={enableSelectAll && multiple ? renderOptionWithSelectAll : renderOption}
        size={size}
        slotProps={{ chip: { deleteIcon, variant: 'contained' } }}
      />
    </Box>
  );
};

export default Autocomplete;
